'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FileText, Shield, Users, AlertTriangle, Scale, Globe, Mail, Clock, Building } from 'lucide-react';
import Footer from '@/app/public/component/footer';

export default function LegalNoticePage() {
  return (
    <div className="h-dvh bg-linear-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 w-dvw overflow-y-scroll">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Scale className="h-8 w-8 text-blue-600" />
            <h1 className="text-4xl font-bold bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Legal Notice
            </h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Your guide to navigating our legal terms and conditions for <span className="font-semibold text-blue-600">LitePop</span> by <span className="font-semibold text-purple-600">DeepZone</span>
          </p>
          <Badge variant="outline" className="mt-4">
            <Clock className="h-3 w-3 mr-1" />
            Last updated: {new Date().toLocaleDateString()}
          </Badge>
        </div>

        {/* Quick Summary */}
        <Card className="mb-8 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Legal Summary
            </CardTitle>
            <CardDescription>
              Key legal points you should know
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm">• You're prohibited from using the site for illegal purposes</p>
                <p className="text-sm">• You're responsible for content you submit</p>
                <p className="text-sm">• Disputes resolved through Australian arbitration</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm">• DeepZone not liable for damages from site use</p>
                <p className="text-sm">• You agree to indemnify us from third-party claims</p>
                <p className="text-sm">• Contact us at privacy@deepzone.com</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              <a href="#who-we-are" className="text-blue-600 hover:underline">1. Who We Are</a>
              <a href="#intellectual-property" className="text-blue-600 hover:underline">2. Intellectual Property</a>
              <a href="#responsibility" className="text-blue-600 hover:underline">3. Responsibility</a>
              <a href="#personal-data" className="text-blue-600 hover:underline">4. Protection of Personal Data</a>
              <a href="#cookies" className="text-blue-600 hover:underline">5. Cookies</a>
              <a href="#applicable-law" className="text-blue-600 hover:underline">6. Applicable Law and Jurisdiction</a>
            </div>
          </CardContent>
        </Card>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Welcome to Our Legal Notice
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              Welcome to the Legal Notice page of DeepZone. Here, you will find comprehensive information regarding the terms of use, user rights, and responsibilities associated with our service. This section is designed to keep you informed and ensure a clear understanding of the policies that safeguard your experience with LitePop.
            </p>
            <p className="text-sm leading-relaxed">
              By familiarizing yourself with these legal details, you can confidently navigate our platform, knowing that your safety and privacy are our top priorities.
            </p>
          </CardContent>
        </Card>

        {/* Who We Are */}
        <Card id="who-we-are" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-purple-500" />
              1. Who We Are
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              You are accessing the website of DeepZone ("DeepZone", the "Company" or "we/us") available at our LitePop platform (hereinafter the "Website"). You can contact us at:
            </p>
            <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                Email: privacy@deepzone.com
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Intellectual Property */}
        <Card id="intellectual-property" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-500" />
              2. Intellectual Property
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              All rights of intellectual property on any contents of this Website including, without limitation, images, trademarks, logos, distinctive signs, sounds and animations, texts, sound, visual and audio-visual recordings, and the databases, are owned by the Company or its licensors and are protected by national and international laws.
            </p>
            <p className="text-sm leading-relaxed">
              Such rights are reserved in favour of the Company and/or its licensors. Any reproduction, copying, publication, distribution, modification, transformation, removal, handling, and any other use, with or without profit, all or part of this Website or any of its contents, without the prior express written permission of the Company is expressly prohibited.
            </p>
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                DeepZone may exercise any legal and/or extrajudicial actions it considers appropriate, in case of a possible violation of our rights.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Responsibility */}
        <Card id="responsibility" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              3. Responsibility
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-sm">Third-Party Content</h4>
            <p className="text-sm leading-relaxed">
              DeepZone is not responsible for any third-party webpage that are accessible from the Website, nor of the accuracy, veracity and validity of the Website's information that is not of its own elaboration.
            </p>
            <p className="text-sm leading-relaxed">
              In the event that DeepZone becomes aware of the unlawfulness of such content or links, it undertakes to suppress or disable the links, as well as, where appropriate, blocking those contents that may be unlawful or infringe the rights of any third-party.
            </p>
            
            <h4 className="font-semibold text-sm">Technical Issues</h4>
            <p className="text-sm leading-relaxed">
              Furthermore, the Company will not be responsible for the damages that may occur due to failures or bad configurations of the browser installed by the user in its computer. DeepZone will not be responsible for any technical incident or failure that occurs when the user connects to the Internet. Likewise, the absence of interruptions or errors in accessing the website is not guaranteed.
            </p>
            
            <h4 className="font-semibold text-sm">Website Updates</h4>
            <p className="text-sm leading-relaxed">
              DeepZone reserves the right to update, modify or delete the information contained in its website, as well as its configuration or presentation, at any time without assuming any responsibility for it.
            </p>
          </CardContent>
        </Card>

        {/* Protection of Personal Data */}
        <Card id="personal-data" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              4. Protection of Personal Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              We will process your personal data collected on our Website or when you contact us via email according to our Privacy Policy.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                For detailed information about how we handle your personal data, please refer to our comprehensive Privacy Policy.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card id="cookies" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-500" />
              5. Cookies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              In our Website we use cookies, please see our Cookie Policy for further details.
            </p>
            <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Our Cookie Policy provides comprehensive information about the types of cookies we use and how you can manage your cookie preferences.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Applicable Law and Jurisdiction */}
        <Card id="applicable-law" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-purple-500" />
              6. Applicable Law and Jurisdiction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              These Legal Terms, including our Privacy Policy and Cookie Policy, are governed by Australian laws.
            </p>
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                All legal matters relating to your use of LitePop will be subject to the jurisdiction of Australian courts.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-green-500" />
              Contact Us
            </CardTitle>
            <CardDescription>
              Questions about this Legal Notice?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">
              If you have any questions or concerns about this Legal Notice or need further clarification on any legal matters, please don't hesitate to contact us:
            </p>
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Email: privacy@deepzone.com
              </p>
            </div>
          </CardContent>
        </Card>

        <Separator className="my-8" />
        
        <div className="text-center text-sm text-muted-foreground">
          <p>© 2024 DeepZone. All rights reserved.</p>
          <p className="mt-2">This Legal Notice was last updated on {new Date().toLocaleDateString()}.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}